/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Typography, Divider, Calendar, message, Space } from 'antd';
import emiter, { _EVENTS } from '../../../../events';
import moment from 'moment';
import EditPrice from '../edit-price';
import WeekendPrice from '../weekend-price';
import IntervalPrice from '../interval-price';
import { useCarModelPrice } from '../../../../hooks/product-price/car-model-price';
import { jsonParseData } from '../../../../utils';

const { Text, Title } = Typography;

interface PropsType {
	enums?: any;
	title?: any;
	detail?: any;
	disabled?: any;
}

const selectedBorderStyle: React.CSSProperties = {
	borderLeft: '1px solid #e5e5e5',
	borderRight: '1px solid #e5e5e5',
};

const commonStyle: React.CSSProperties = {
	cursor: 'pointer',
	borderTop: '1px solid #e5e5e5',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	flexDirection: 'column',
	height: '80px',
	width: '96%',
	position: 'relative',
	userSelect: 'none',
};

const isSelectedStyle: React.CSSProperties = {
	background: '#fff2e3',
	transition: 'background 0.3s',
};

const isNoSelectedStyle: React.CSSProperties = {
	background: '#fff',
};

function getDate(datestr: string) {
	var temp: any = datestr.split('-');
	var date = new Date(temp[0], temp[1] - 1, temp[2]);
	return date;
}

function getAll(start: any, end: any) {
	let dateList = [];
	var startTime = getDate(start);
	var endTime = getDate(end);

	while (endTime.getTime() - startTime.getTime() >= 0) {
		var year = startTime.getFullYear();
		var month = startTime.getMonth() + 1 < 10 ? '0' + (startTime.getMonth() + 1) : startTime.getMonth() + 1;
		var day = startTime.getDate().toString().length === 1 ? '0' + startTime.getDate() : startTime.getDate();
		dateList.push(year + '-' + month + '-' + day);
		startTime.setDate(startTime.getDate() + 1);
	}
	return dateList;
}

const PriceCalendar: React.FC<PropsType> = props => {
	const { title, detail, disabled, enums } = props;
	const [selectedDates, setSelectedDates] = useState<any>([]);
	const [value, setValue] = useState(moment());
	const [isDragging, setIsDragging] = useState(false);
	const [dragStartDate, setDragStartDate] = useState(null);
	const [draggedDates, setDraggedDates] = useState<any>([]);
	const [holidayList, setHolidayList] = useState([]);
	const [showBorder, setShowBorder] = useState(false);
	const [startMousePosition, setStartMousePosition] = useState<any>(null);
	const DRAG_THRESHOLD = 10; // 滑动灵敏度阈值

	const { getSaasApolloConfig } = useCarModelPrice();

	// 跳转到上个月
	const goToPrevMonth = () => {
		setValue(currentValue => {
			const newDate = moment(currentValue).subtract(1, 'month');
			const threeMonthsAgo = moment().subtract(3, 'months');
			return newDate.isBefore(threeMonthsAgo) ? threeMonthsAgo : newDate;
		});
	};

	// 跳转到下个月
	const goToNextMonth = () => {
		setValue(currentValue => {
			const newDate = moment(currentValue).add(1, 'month');
			const threeMonthsLater = moment().add(6, 'months');
			return newDate.isAfter(threeMonthsLater) ? threeMonthsLater : newDate;
		});
	};

	// 跳转到今天
	const goToToday = () => {
		setValue(moment());
		// 并选中今天的日期
		setShowBorder(true);
	};

	// 更新显示的日期
	const onPanelChange = (newValue: any) => {
		setValue(newValue);
	};

	// 清空选择的日期
	const clearDates = () => {
		setSelectedDates([]);
	};

	const disabledDate = (current: any) => {
		const startOfCurrentMonth = moment().startOf('month');
		const endOfThreeMonthsLater = moment().add(6, 'months').endOf('month');
		const today = moment().startOf('day');

		// 禁用当月当前日期之前的日期和除当前月份和后三个月之外的日期
		return (current && current < today) || current < startOfCurrentMonth || current > endOfThreeMonthsLater;
	};

	const handleMouseDown = useCallback(
		(date: any, event: any) => {
			if (disabled) {
				return;
			}
			setDragStartDate(date);
			setStartMousePosition({ x: event.clientX, y: event.clientY });
			setIsDragging(false);
		},
		[disabled]
	);

	const handleMouseUp = useCallback(
		(date: any) => {
			if (disabled) {
				return;
			}
			if (isDragging) {
				// 拖动结束后合并拖动选择的日期到选中的日期中
				setSelectedDates((prevDates: any) => {
					const updatedDates = Array.from(new Set([...prevDates, ...draggedDates]));
					return updatedDates;
				});
			} else {
				// 处理单击事件
				const dateString = date.format('YYYY-MM-DD');
				setSelectedDates((prevDates: any) =>
					prevDates.includes(dateString)
						? prevDates.filter((d: any) => d !== dateString)
						: [...prevDates, dateString]
				);
			}
			setIsDragging(false);
			setDragStartDate(null);
			setDraggedDates([]);
			setStartMousePosition(null);
		},
		[disabled, isDragging, draggedDates]
	);

	const handleMouseMove = useCallback(
		(date: any, event: any) => {
			if (dragStartDate && startMousePosition) {
				const distanceX = Math.abs(event.clientX - startMousePosition.x);
				const distanceY = Math.abs(event.clientY - startMousePosition.y);

				if (distanceX > DRAG_THRESHOLD || distanceY > DRAG_THRESHOLD) {
					if (!isDragging) {
						setIsDragging(true);
						setSelectedDates([]); // 清空之前选中的日期
					}
					const startDate = moment.min(dragStartDate, date);
					const endDate = moment.max(dragStartDate, date);
					const datesInRange = [];

					let currentDate = startDate.clone();
					while (currentDate <= endDate) {
						datesInRange.push(currentDate.format('YYYY-MM-DD'));
						currentDate.add(1, 'days');
					}

					setSelectedDates(datesInRange); // 实时更新选中的日期
				}
			}
		},
		[dragStartDate, startMousePosition, isDragging]
	);

	// 更改价格显示
	const chengePriceFormat = (price: any) => {
		if (!price) return price;
		if (!isNaN(Number(price))) {
			price = price / 100;
		}
		return price;
	};

	// 自定义日期单元格渲染
	const dateFullCellRender = (current: any) => {
		let dayOfWeek = current.day();
		if (dayOfWeek === 0) {
			dayOfWeek = 7;
		}
		const dateString = current.format('YYYY-MM-DD');
		const currentMonth = moment().month();
		const currentYear = moment().year();
		let currPrice = '';
		let color = '#000';
		let holidayName = '';

		if (detail && detail.price_detail) {
			if (detail.price_detail?.weekday) {
				// 周中
				if (detail.price_detail.weekday?.day?.length) {
					const days = detail.price_detail.weekday.day;
					if (days.includes(dayOfWeek)) {
						currPrice = detail.price_detail.weekday.price || '';
					}
				}
			}
			if (detail.price_detail?.weekend) {
				// 周末
				if (detail.price_detail.weekend?.day?.length) {
					const days = detail.price_detail.weekend.day;
					if (days.includes(dayOfWeek)) {
						currPrice = detail.price_detail.weekend.price || '';
						color = '#108ee9';
					}
				}
			}
			if (detail.price_detail?.holiday) {
				// 节假日
				if (detail.price_detail.holiday?.length) {
					const days = detail.price_detail.holiday;
					days.forEach((item: any) => {
						if (item.day === dateString) {
							currPrice = item.price || '';
							color = 'red';
						}
					});
				}
			}
		}

		if (holidayList.length) {
			holidayList.forEach((item: any) => {
				if (item.date_ranges && item.date_ranges.length) {
					const allHoliday = getAll(item.date_ranges[0], item.date_ranges[1]);
					allHoliday &&
						allHoliday.forEach((everyDay: any) => {
							if (dateString === everyDay) {
								holidayName = item.name;
							}
						});
				}
			});
		}

		// 历史日期不需要展示价格
		const isBeforeToday = moment(dateString).isBefore(moment(), 'day');
		if (isBeforeToday) {
			currPrice = '';
		}

		const isSelected = selectedDates.includes(dateString);
		let finalStyle = isSelected ? { ...commonStyle, ...isSelectedStyle } : { ...commonStyle, ...isNoSelectedStyle };
		const isToday =
			dateString === moment().format('YYYY-MM-DD') &&
			current.month() === currentMonth &&
			current.year() === currentYear;
		if (showBorder && isToday) {
			finalStyle = { ...finalStyle, ...selectedBorderStyle };
		}
		return (
			<div
				style={finalStyle}
				onMouseDown={e => handleMouseDown(current, e)}
				onMouseUp={() => handleMouseUp(current)}
				onMouseMove={e => handleMouseMove(current, e)}
			>
				<span
					style={{
						position: 'absolute',
						top: '10px',
						left: '10px',
					}}
				>
					{current.date()}
				</span>
				<div style={{ color: color }}>{chengePriceFormat(currPrice)}</div>
				<div style={{ fontSize: '12px', marginTop: '5px' }}>{holidayName}</div>
			</div>
		);
	};

	// 修改价格弹窗唤起
	const editPrice = () => {
		if (!selectedDates || !selectedDates.length) {
			message.error('请先选择日期');
			return;
		}
		emiter.emit(_EVENTS.PRODUCT_PRICE.CAR_MODEL_PRICE.EDIT_PRICE, selectedDates);
	};

	// 周末周中定价
	const openWeekend = () => {
		emiter.emit(_EVENTS.PRODUCT_PRICE.CAR_MODEL_PRICE.WEEKEND_PRICE, { title });
	};

	useEffect(() => {
		const handleMouseUpGlobal = () => {
			if (isDragging) {
				// 拖动结束后合并拖动选择的日期到选中的日期中
				setSelectedDates((prevDates: any) => {
					const draggedDateStrings = draggedDates.map((d: any) => d.format('YYYY-MM-DD'));
					const updatedDates = Array.from(new Set([...prevDates, ...draggedDateStrings]));
					return updatedDates;
				});
			}
			setIsDragging(false);
			setDragStartDate(null);
			setDraggedDates([]);
		};

		window.addEventListener('mouseup', handleMouseUpGlobal);
		return () => {
			window.removeEventListener('mouseup', handleMouseUpGlobal);
		};
	}, []);

	// 节假日区间定价弹窗唤起
	const openHoliday = () => {
		emiter.emit(_EVENTS.PRODUCT_PRICE.CAR_MODEL_PRICE.INTERVAL_PRICE);
	};

	useEffect(() => {
		emiter.on(_EVENTS.PRODUCT_PRICE.CAR_MODEL_PRICE.CLEAR_DATES, clearDates);
		return () => {
			emiter.removeAllListeners(_EVENTS.PRODUCT_PRICE.CAR_MODEL_PRICE.CLEAR_DATES)
		}
	}, []);

	useEffect(() => {
		getSaasApolloConfig({
			config_name: 'front_enums',
			param_name: 'f_holiday_enum',
		}).then((res: any) => {
			setHolidayList(jsonParseData(res?.data?.f_holiday_enum, []));
		});
	}, []);

	return (
		<>
			<Title level={5}>{title}</Title>
			{disabled ? null : (
				<div style={{ textAlign: 'right' }}>
					<Space>
						<Button type="primary" onClick={openWeekend}>
							周末周中定价
						</Button>
						<Button type="primary" onClick={openHoliday}>
							区间定价
						</Button>
					</Space>
				</div>
			)}
			<Divider />
			<div style={{ display: 'flex', justifyContent: 'space-between' }}>
				<div>{moment(value).format('YYYY年MM月')}</div>
				<div>
					<Button onClick={goToPrevMonth}>上个月</Button>
					<Button onClick={goToToday}>今天</Button>
					<Button onClick={goToNextMonth}>下个月</Button>
				</div>
			</div>
			<Divider />
			<Calendar
				headerRender={() => null}
				value={value}
				disabledDate={disabledDate}
				dateFullCellRender={dateFullCellRender}
				onPanelChange={onPanelChange}
			/>
			<Divider />
			<div style={{ display: 'flex', justifyContent: 'space-between' }}>
				<div>
					<Text>价格颜色含义</Text>
					<div>
						<span style={{ color: '#108ee9' }}>蓝色：周末价</span>；
						{/* <span style={{ color: '#fc1e2a' }}>红色：手动调整过的价格</span>； */}
						<span style={{ color: 'red' }}>红色：节假日&手动改价格</span>；<span>黑色：平日价</span>
					</div>
				</div>
				{disabled ? null : (
					<div>
						<Button type="primary" onClick={editPrice}>
							修改
						</Button>
					</div>
				)}
			</div>
			<EditPrice detail={detail} />
			<WeekendPrice detail={detail} enums={enums} />
			<IntervalPrice detail={detail} />
		</>
	);
};

export default PriceCalendar;
